import { z } from "zod";
import { expenseCategorySchema } from "./category.js";
import { CURRENCIES } from "./money.js";

/**
 * Le statistiche di un gruppo.
 *
 * **I pareggi non entrano.** Un pagamento fra due persone non è una spesa: non
 * compra niente e non aumenta quanto il gruppo ha speso, sposta solo chi deve
 * cosa a chi. Contarlo gonfierebbe il totale ogni volta che qualcuno salda, e
 * un «Prestito» da 1.000 € comparirebbe come la voce più grossa dell'anno pur
 * non essendo una spesa di nessuno.
 *
 * Le spese eliminate non entrano allo stesso modo: sono fuori dai saldi, e
 * devono essere fuori anche da qui.
 */
export const STATS_PERIODS = ["6m", "year", "all"] as const;

export type StatsPeriod = (typeof STATS_PERIODS)[number];

export const statsPeriodSchema = z.enum(STATS_PERIODS);

/** Quante barre stanno su uno schermo di telefono senza diventare illeggibili. */
export const STATS_MAX_MONTHS = 12;

export const categoryStatSchema = z.object({
  category: expenseCategorySchema,
  totalCents: z.number().int().nonnegative(),
  /** Quota sul totale del periodo, fra 0 e 1. Calcolata dal server per non
   *  farla divergere fra chi la mostra in lista e chi in un grafico. */
  share: z.number().min(0).max(1),
  expenseCount: z.number().int().nonnegative(),
});

export type CategoryStat = z.infer<typeof categoryStatSchema>;

export const monthStatSchema = z.object({
  /** `YYYY-MM`, non una data: è un contenitore, non un istante. */
  month: z.string().regex(/^\d{4}-\d{2}$/),
  totalCents: z.number().int().nonnegative(),
});

export type MonthStat = z.infer<typeof monthStatSchema>;

export const memberStatSchema = z.object({
  memberId: z.string(),
  displayName: z.string(),
  /** Quanto ha anticipato di tasca propria. */
  paidCents: z.number().int().nonnegative(),
  /** Quanto gli è toccato, cioè la somma delle sue quote. */
  owedCents: z.number().int().nonnegative(),
});

export type MemberStat = z.infer<typeof memberStatSchema>;

/** Un mese come `YYYY-MM`. È un filtro, non un istante. */
export const statsMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);

export const groupStatsResponseSchema = z.object({
  currency: z.enum(CURRENCIES),
  period: statsPeriodSchema,
  /** Estremi effettivi del periodo. `null` su «sempre». */
  from: z.iso.datetime().nullable(),

  /**
   * Il mese a cui sono ristretti i totali, le categorie e le persone.
   *
   * `byMonth` **non** lo rispetta, di proposito: il grafico è il comando che
   * sceglie il mese, e se si restringesse anche lui resterebbe una barra sola
   * e non ci sarebbe più modo di cambiare idea.
   */
  month: statsMonthSchema.nullable(),

  totalCents: z.number().int().nonnegative(),
  expenseCount: z.number().int().nonnegative(),
  /** Quanto è toccato a chi guarda, nel periodo. */
  myShareCents: z.number().int().nonnegative(),
  /** Quanto ha anticipato chi guarda, nel periodo. */
  myPaidCents: z.number().int().nonnegative(),

  /** Dalla più costosa. Le categorie senza spese non compaiono. */
  byCategory: z.array(categoryStatSchema),
  /** In ordine cronologico, mesi vuoti compresi: un buco in un grafico è
   *  un'informazione, saltarlo lo falserebbe. */
  byMonth: z.array(monthStatSchema),
  byMember: z.array(memberStatSchema),
});

export type GroupStatsResponse = z.infer<typeof groupStatsResponseSchema>;
